import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, CheckCircle, HardHat } from "@phosphor-icons/react";

export default function PpePage() {
  const { hasPerm } = useAuth();
  const manager = hasPerm("ppe_manager", "supervisor", "administrator");
  const [mine, setMine] = useState([]);
  const [issues, setIssues] = useState([]);
  const [types, setTypes] = useState([]);
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ profile_id: "", ppe_type_id: "", size: "", quantity: 1, serial_number: "", condition: "New", inspection_due: "", replacement_due: "" });

  const loadMine = useCallback(() => { api.get("/ppe/mine").then((r) => setMine(r.data)).catch(() => {}); }, []);
  const loadIssues = useCallback(() => { if (manager) api.get("/ppe/issues").then((r) => setIssues(r.data)).catch(() => {}); }, [manager]);
  useEffect(() => {
    loadMine(); loadIssues();
    if (manager) {
      api.get("/config/ppe-types").then((r) => setTypes(r.data)).catch(() => {});
      api.get("/people").then((r) => setPeople(r.data)).catch(() => {});
    }
  }, [loadMine, loadIssues, manager]);

  const confirm = async (id) => { try { await api.post(`/ppe/${id}/acknowledge`, {}); toast.success("Receipt confirmed"); loadMine(); loadIssues(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const issue = async () => {
    try { await api.post("/ppe/issue", form); toast.success("PPE issued"); setOpen(false);
      setForm({ profile_id: "", ppe_type_id: "", size: "", quantity: 1, serial_number: "", condition: "New", inspection_due: "", replacement_due: "" });
      loadIssues(); loadMine(); }
    catch (e) { toast.error("Failed"); }
  };

  const MineList = ({ items }) => (
    <div className="flex flex-col gap-2">
      {items.map((i) => (
        <div key={i.id} data-testid={`ppe-mine-${i.id}`} className={`bg-white border-2 rounded-lg p-4 flex items-center justify-between border-l-8 ${i.employee_ack_at ? "border-l-emerald-500 border-slate-200" : "border-l-amber-500 border-amber-200"}`}>
          <div>
            <div className="font-body font-semibold text-slate-900 flex items-center gap-2"><HardHat size={18} weight="bold" className="text-slate-500" />{i.ppe_name} {i.size ? `· ${i.size}` : ""}</div>
            <div className="font-mono text-xs text-slate-500">Qty {i.quantity}{i.serial_number ? ` · SN ${i.serial_number}` : ""}{i.replacement_due ? ` · replace ${i.replacement_due}` : ""}</div>
          </div>
          {i.employee_ack_at
            ? <span className="inline-flex items-center gap-1 text-xs font-bold uppercase text-emerald-600"><CheckCircle size={16} weight="fill" />Confirmed</span>
            : <Button data-testid={`ppe-confirm-${i.id}`} onClick={() => confirm(i.id)} className="bg-emerald-600 hover:bg-emerald-700 font-semibold">Confirm Receipt</Button>}
        </div>
      ))}
      {items.length === 0 && <p className="text-slate-400">Nothing issued to you.</p>}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">PPE</h1>
        {manager && <Button data-testid="issue-ppe" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 font-semibold"><Plus size={18} weight="bold" className="mr-1" />Issue PPE</Button>}
      </div>

      {manager ? (
        <Tabs defaultValue="mine">
          <TabsList>
            <TabsTrigger value="mine" data-testid="ppe-tab-mine">My PPE</TabsTrigger>
            <TabsTrigger value="all" data-testid="ppe-tab-all">All Issues</TabsTrigger>
          </TabsList>
          <TabsContent value="mine"><MineList items={mine} /></TabsContent>
          <TabsContent value="all">
            <div className="bg-white border-2 border-slate-300 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 border-b-2 border-slate-200 text-left font-body text-slate-600"><tr>
                  <th className="px-3 py-2">Item</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2">Serial</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Receipt</th></tr></thead>
                <tbody>
                  {issues.map((i) => (
                    <tr key={i.id} className="border-b border-slate-100">
                      <td className="px-3 py-2 font-semibold text-slate-800">{i.ppe_name} {i.size ? `· ${i.size}` : ""}</td>
                      <td className="px-3 py-2">{i.quantity}</td>
                      <td className="px-3 py-2 font-mono">{i.serial_number || "—"}</td>
                      <td className="px-3 py-2">{i.status}</td>
                      <td className="px-3 py-2">{i.employee_ack_at ? <span className="text-emerald-600 font-semibold">Confirmed</span> : <span className="text-amber-600 font-semibold">Pending</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      ) : <MineList items={mine} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Issue PPE</DialogTitle></DialogHeader>
          <select data-testid="ppe-person" className="h-10 border-2 border-slate-300 rounded px-2" value={form.profile_id} onChange={(e) => setForm({ ...form, profile_id: e.target.value })}>
            <option value="">Employee…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
          <select data-testid="ppe-type" className="h-10 border-2 border-slate-300 rounded px-2" value={form.ppe_type_id} onChange={(e) => setForm({ ...form, ppe_type_id: e.target.value })}>
            <option value="">PPE type…</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}{t.requires_serial ? " (serial)" : ""}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="ppe-size" placeholder="Size / variant" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
            <Input data-testid="ppe-qty" type="number" placeholder="Quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} />
          </div>
          <Input data-testid="ppe-serial" placeholder="Serial number (if required)" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600">Inspection due</label><Input data-testid="ppe-inspection" type="date" value={form.inspection_due} onChange={(e) => setForm({ ...form, inspection_due: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-slate-600">Replacement due</label><Input data-testid="ppe-replacement" type="date" value={form.replacement_due} onChange={(e) => setForm({ ...form, replacement_due: e.target.value })} /></div>
          </div>
          <Button data-testid="ppe-issue-submit" onClick={issue} disabled={!form.profile_id || !form.ppe_type_id} className="bg-blue-600 hover:bg-blue-700 font-semibold">Issue</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
